import { api } from './api';

// Spec 049 — partner Earnings. Garage is resolved server-side from the token; no garageId passed.

export interface EarningsBalance {
  nextPayout: number;
  inProgress: number;
  paidToDate: number;
  onHold: number;
  currency: string;
  // Spec 053 AC-21d — shortfall carried from earlier cycles, deducted from the next payout.
  // Positive = the garage owes Garagat; absent/0 = nothing carried.
  carriedBalance?: number;
  carriedBalanceNote?: string;
  // Spec 067 — Tabby-paid orders still in the Tabby holding period. Not in nextPayout or any other total.
  awaitingTabbySettlement?: AwaitingTabbySettlement;
}

export interface AwaitingTabbySettlement {
  count: number;
  grossAmount: number;
  /** When the first of these orders is released (ISO instant); null when there are none. */
  earliestEligibleAt: string | null;
}

export interface PartnerSettlement {
  id: string;
  settlementNumber: string;
  periodStart: string;
  periodEnd: string;
  orderCount: number;
  netPayable: number;
  currency: string;
  status: string; // partner-safe label (Preparing / Approved – payment in progress / Paid / On hold)
  paymentReference: string | null;
  paidAt: string | null;
  createdAt: string;
}

export interface SettlementLineItem {
  orderId: string;
  orderNumber: string | null;
  orderDate: string;
  customerName: string | null;
  serviceSummary: string | null;
  grossAmount: number;
  commissionRate: number | null;
  commissionAmount: number | null;
  vatOnCommission: number | null;
  netAmount: number | null;
  isRefunded: boolean;
  refundAmount: number | null;
  refundReason: string | null;
  /** Spec 053 (#470, AC-9) — commission (+VAT) reversed on this line by a refund; 0 when none. */
  reversedCommission: number;
  reversedVat: number;
  /** Spec 067 — the Tabby fee (net of VAT) the garage bears on this order, and its VAT; 0 for card. */
  garageFeeDeduction?: number;
  garageFeeVat?: number;
  /** Spec 068 — the service fee recharged to the garage on this order (GARAGE-fault refund), and its VAT. */
  serviceFeeDeduction?: number | null;
  serviceFeeDeductionVat?: number | null;
}

export interface SettlementDetail {
  id: string;
  settlementNumber: string;
  garageName: string;
  periodStart: string;
  periodEnd: string;
  orderCount: number;
  grossRevenue: number;
  commissionTotal: number;
  vatOnCommission: number;
  refundDeductions: number;
  /** Spec 067 — Σ signed adjustments in this settlement (credits +, debits −). */
  adjustmentsTotal?: number;
  /** Spec 067 — Tabby fees the garage bears (net of VAT, positive) and the VAT on them (positive). */
  gatewayFeeDeductions?: number;
  gatewayFeeVat?: number;
  /**
   * Spec 068 — "Service fee recharges": service fees refunded to customers for the garage's fault,
   * deducted here (positive), and the VAT on them (positive). 0.00/null when there are none.
   */
  serviceFeeDeductions?: number | null;
  serviceFeeDeductionsVat?: number | null;
  /** Legacy alias of carriedBalanceOut (≤ 0): the shortfall this settlement rolls to the next one. */
  carriedBalance: number;
  /** Shortfall rolled in from the previous settlement (≤ 0). */
  carriedBalanceIn?: number;
  carriedBalanceOut?: number;
  netPayable: number;
  currency: string;
  status: string;
  settledAt: string | null;
  createdAt: string;
  lineItems: SettlementLineItem[];
}

// Spec 051 AC-22 — commission tax invoice availability + presigned download.
// AVAILABLE: PDF ready; GENERATING: settlement PAID but PDF still rendering;
// NOT_AVAILABLE: pre-PAID (no document exists — hide the button).
export interface TaxInvoiceUrlResponse {
  status: 'AVAILABLE' | 'GENERATING' | 'NOT_AVAILABLE';
  downloadUrl: string | null;
  invoiceNumber: string | null;
}

// Spec 053 AC-5 — commission invoices (INV) and credit notes (CCN) issued to this garage.
// downloadUrl is presigned and may be null while the PDF is still rendering.
export type PartnerDocumentType = 'INV' | 'CCN';

export interface PartnerDocument {
  type: PartnerDocumentType;
  number: string;
  date: string;
  amount: number;
  settlementNumber: string | null;
  downloadUrl: string | null;
}

export const earningsApi = {
  getBalance: (token: string) =>
    api.get<EarningsBalance>('/api/settlements/me/balance', token),

  listSettlements: (token: string) =>
    api.get<PartnerSettlement[]>('/api/settlements/me', token),

  getSettlement: (id: string, token: string) =>
    api.get<SettlementDetail>(`/api/settlements/me/${id}`, token),

  // NOTE: the settlement STATEMENT endpoint intentionally does not exist (deferred
  // from Spec 049); the statement button was removed from the detail page. Do not
  // re-add a getStatementUrl call until the backend ships one.

  getTaxInvoiceUrl: (id: string, token: string) =>
    api.get<TaxInvoiceUrlResponse>(`/api/settlements/me/${id}/tax-invoice-url`, token),

  getDocuments: (token: string) =>
    api.get<PartnerDocument[]>('/api/settlements/me/documents', token),
};
